import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { ApplicationService } from "./useApplicationServices";

const API_URL = `${process.env.REACT_APP_API_URL}/package-service-mappings`;

export interface PackageServiceMappingRequest {
  packageId: number;
  serviceIds: number[];
}

export interface PackageServiceMappingResponse {
  packageId: number;
  packageName: string;
  servicesList: ApplicationService[];
}

export const getServicesByPackage = async (packageId: number): Promise<PackageServiceMappingResponse> => {
  const resp = await axios.get(`${API_URL}/package/${packageId}`);
  return resp.data;
};

const createMapping = async (data: PackageServiceMappingRequest): Promise<PackageServiceMappingResponse> => {
  const resp = await axios.post(API_URL, data);
  return resp.data;
};

const updateMapping = async (data: PackageServiceMappingRequest): Promise<PackageServiceMappingResponse> => {
  const resp = await axios.put(API_URL, data);
  return resp.data;
};

export function usePackageServiceMapping(packageId?: number | null) {
  const queryClient = useQueryClient();

  const mappingQuery = useQuery<PackageServiceMappingResponse, Error>({
    queryKey: ["package-service-mapping", packageId],
    queryFn: () => getServicesByPackage(packageId!),
    enabled: !!packageId,
  });

  const createMappingMutation = useMutation({
    mutationFn: createMapping,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["package-service-mapping", variables.packageId] });
    },
  });

  const updateMappingMutation = useMutation({
    mutationFn: updateMapping,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["package-service-mapping", variables.packageId] });
    },
  });

  return {
    mappingQuery,
    createMappingMutation,
    updateMappingMutation,
    getServicesByPackage,
  };
}
